
/// <amd-module name="Build/Settings/retention/RetentionSettingsStore" />
import { ProjectRetentionSettingData, RetentionSettingData } from 'Build/Client/Contracts/DataProvider';
import { ContributionSource } from 'Build/Flux/Sources/Contribution';
import { Store } from "Build/Flux/Store";
import { ObservableValue } from "VSS/Core/Observable";
import { IVssPageContext } from 'VSS/Platform/Context';

export interface IRetentionSettingsStoreOptions {
    pageContext: IVssPageContext;
}

interface ISettingState {
    value: ObservableValue<string>;
    isValid: boolean;
    serverSetting: RetentionSettingData;
}

export class RetentionSettingsStore extends Store {
    private _pageIsReadOnly: boolean;
    private _showSettings: boolean;

    private _purgeArtifactsIgnored: boolean;
    private _purgeArtifactsSetting: ISettingState;
    private _purgeRunsSetting: ISettingState;
    private _purgePullRequestRunsSetting: ISettingState;
    private _runsToRetainPerProtectedBranchSetting: ISettingState;

    private _source: ContributionSource;

    public constructor(options: IRetentionSettingsStoreOptions) {
        super();

        this._source = new ContributionSource({
            pageContext: options.pageContext
        });

        this._pageIsReadOnly = true;
        this._showSettings = true;

        this._purgeArtifactsSetting = {
            value: new ObservableValue<string>(""),
            isValid: false,
            serverSetting: {
                min: 0,
                max: 0,
                value: 0
            }
        };

        this._purgeRunsSetting = {
            value: new ObservableValue<string>(""),
            isValid: false,
            serverSetting: {
                min: 0,
                max: 0,
                value: 0
            }
        };

        this._purgePullRequestRunsSetting = {
            value: new ObservableValue<string>(""),
            isValid: false,
            serverSetting: {
                min: 0,
                max: 0,
                value: 0
            }
        };

        this._runsToRetainPerProtectedBranchSetting = {
            value: new ObservableValue<string>(""),
            isValid: false,
            serverSetting: {
                min: 0,
                max: 0,
                value: 0
            }
        }

        this._purgeArtifactsSetting.value.subscribe(this._onPurgeArtifactsChanged);
        this._purgeRunsSetting.value.subscribe(this._onPurgeRunsChanged);
        this._purgePullRequestRunsSetting.value.subscribe(this._onPurgePullRequestRunsChanged);
        this._runsToRetainPerProtectedBranchSetting.value.subscribe(this._onrunsToRetainPerProtectedBranchChanged);
    }

    public dispose() {
        this._purgeArtifactsSetting.value.unsubscribe(this._onPurgeArtifactsChanged);
        this._purgeRunsSetting.value.unsubscribe(this._onPurgeRunsChanged);
        this._purgePullRequestRunsSetting.value.unsubscribe(this._onPurgePullRequestRunsChanged);
        this._runsToRetainPerProtectedBranchSetting.value.unsubscribe(this._onrunsToRetainPerProtectedBranchChanged);
    }

    public get isReadOnly() {
        return this._pageIsReadOnly;
    }

    public get showSettings() {
        return this._showSettings;
    }

    public get isValid() {
        return (
            !this.purgeArtifactsInvalid &&
            !this.purgePullRequestRunsInvalid &&
            !this.purgeRunsInvalid &&
            !this.runsToRetainPerProtectedBranchInvalid);
    }

    public get purgeArtifactsAfter(): ObservableValue<string> {
        return this._purgeArtifactsSetting.value;
    }

    public get purgeArtifactsIgnored(): boolean {
        return this._purgeArtifactsIgnored;
    }

    public get purgeArtifactsInvalid(): boolean {
        return !this._purgeArtifactsSetting.isValid;
    }

    public get purgeArtifactsSetting(): RetentionSettingData {
        return this._purgeArtifactsSetting.serverSetting;
    }

    public get purgeRunsAfter(): ObservableValue<string> {
        return this._purgeRunsSetting.value;
    }

    public get purgeRunsInvalid(): boolean {
        return !this._purgeRunsSetting.isValid;
    }

    public get purgeRunsSetting(): RetentionSettingData {
        return this._purgeRunsSetting.serverSetting;
    }

    public get purgePullRequestRunsAfter(): ObservableValue<string> {
        return this._purgePullRequestRunsSetting.value;
    }

    public get purgePullRequestRunsInvalid(): boolean {
        return !this._purgePullRequestRunsSetting.isValid;
    }

    public get purgePullRequestRunsSetting(): RetentionSettingData {
        return this._purgePullRequestRunsSetting.serverSetting;
    }

    public get runsToRetainPerProtectedBranch(): ObservableValue<string> {
        return this._runsToRetainPerProtectedBranchSetting.value;
    }

    public get runsToRetainPerProtectedBranchInvalid(): boolean {
        return !this._runsToRetainPerProtectedBranchSetting.isValid;
    }

    public get runsToRetainPerProtectedBranchSetting(): RetentionSettingData {
        return this._runsToRetainPerProtectedBranchSetting.serverSetting;
    }

    public async fetch() {
        await this._update(undefined);
    }

    private async _update(routeData: { [key: string]: string } | undefined) {
        this._pageIsReadOnly = true;
        this.emitChanged();
        const retentionSettings = await this._source.getDataAsync<ProjectRetentionSettingData>(this.c_contributionId, routeData, true);
        if (!!retentionSettings) {
            this._showSettings = true;
            this._purgeArtifactsSetting.serverSetting = retentionSettings.purgeAllArtifacts;
            this._purgePullRequestRunsSetting.serverSetting = retentionSettings.purgePullRequestRuns;
            this._purgeRunsSetting.serverSetting = retentionSettings.purgeRuns;
            this._runsToRetainPerProtectedBranchSetting.serverSetting = retentionSettings.runsToRetainPerProtectedBranch;

            this._purgeArtifactsSetting.value.value = this._purgeArtifactsSetting.serverSetting.value.toString();
            this._purgeRunsSetting.value.value = this._purgeRunsSetting.serverSetting.value.toString();
            this._purgePullRequestRunsSetting.value.value = this._purgePullRequestRunsSetting.serverSetting.value.toString();
            this._runsToRetainPerProtectedBranchSetting.value.value = this._runsToRetainPerProtectedBranchSetting.serverSetting.value.toString();

            // assume the server values are valid
            this._purgeArtifactsSetting.isValid = true;
            this._purgeRunsSetting.isValid = true;
            this._purgePullRequestRunsSetting.isValid = true;
            this._runsToRetainPerProtectedBranchSetting.isValid = true;

            this._pageIsReadOnly = retentionSettings.viewOnlyPermissions;

            this._purgeArtifactsIgnored = this._purgeArtifactsSetting.serverSetting.value >= this._purgeRunsSetting.serverSetting.value;
        }
        else {
            this._showSettings = false;
            this._pageIsReadOnly = true;
        }

        this.emitChanged();
    }

    private _onPurgeArtifactsChanged = (newVal: string) => {
        this._onValueChanged(
            newVal,
            this._purgeArtifactsSetting);
    }

    private _onPurgePullRequestRunsChanged = (newVal: string) => {
        this._onValueChanged(
            newVal,
            this._purgePullRequestRunsSetting);
    }

    private _onPurgeRunsChanged = (newVal: string) => {
        this._onValueChanged(
            newVal,
            this._purgeRunsSetting);
    }

    private _onrunsToRetainPerProtectedBranchChanged = (newVal: string) => {
        this._onValueChanged(
            newVal,
            this._runsToRetainPerProtectedBranchSetting);
    }

    private _onValueChanged = async (newVal: string, settingState: ISettingState) => {
        if (!this._pageIsReadOnly) {
            const newValInt = parseInt(newVal);

            const isValid = newValInt >= settingState.serverSetting.min && newValInt <= settingState.serverSetting.max;
            if (settingState.isValid != isValid) {
                settingState.isValid = isValid;
                this.emitChanged();
            }

            if (this.isValid && newValInt !== settingState.serverSetting.value) {
                const routeData: { [key: string]: string } = {
                    [this.c_purgeArtifactsAfter]: this.purgeArtifactsAfter.value,
                    [this.c_purgePullRequestRunsAfter]: this.purgePullRequestRunsAfter.value,
                    [this.c_purgeRunsAfter]: this.purgeRunsAfter.value,
                    [this.c_runsToRetainPerProtectedBranch]: this.runsToRetainPerProtectedBranch.value
                };

                await this._update(routeData);
            }
        }
    }

    readonly c_contributionId: string = "ms.vss-build-web.pipelines-retention-data-provider";
    readonly c_purgeRunsAfter: string = "PurgeRuns";
    readonly c_purgePullRequestRunsAfter: string = "PurgePullRequestRuns";
    readonly c_purgeArtifactsAfter: string = "PurgeArtifacts";
    readonly c_runsToRetainPerProtectedBranch: string = "runsToRetainPerProtectedBranch";
}
